import { useState } from "react";
import axios, { API } from "../api";

/**
 * Phase 27.1 — Share-worthy moment nudge pill.
 *
 * Aura's chat response can include a `share_nudge: {kind, blurb}` payload when
 * the LLM classifier decides a moment is worth sharing. This component renders
 * the pill below her message bubble. Tap → open the device share sheet OR fall
 * back to a small menu with Snap / SMS / copy link.
 *
 * Tone is gentle, never pushy. Outcomes (dismissed/tapped/shared) are reported
 * back to the backend to drive the 6-hour-after-2-dismissals backoff.
 */
const KIND_ICONS = {
  funny: "😂",
  milestone: "🎉",
  wholesome: "💜",
  beautiful: "✨",
  achievement: "🏆",
};

export function ShareNudgePill({ nudge, messageContent, onDismissed }) {
  const [dismissed, setDismissed] = useState(false);
  const [opening, setOpening] = useState(false);

  if (!nudge || dismissed) return null;
  const { kind, blurb } = nudge;
  const icon = KIND_ICONS[kind] || "📤";

  const report = (event) => {
    axios.post(`${API}/share-nudge/outcome`, { event, kind }).catch(() => {});
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    report("dismissed");
    if (onDismissed) onDismissed();
  };

  const handleOpen = async () => {
    setOpening(true);
    report("tapped");
    const text = `${messageContent || ""}\n\n— shared from Aura ✨ https://aura.wwwwhynot.com`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "From Aura",
          text,
          url: "https://aura.wwwwhynot.com",
        });
        report("shared");
        setDismissed(true);
      } else {
        // Desktop fallback: copy to clipboard
        await navigator.clipboard.writeText(text);
        report("shared");
        setDismissed(true);
        // Tiny inline confirm so user knows it copied
        try {
          window.dispatchEvent(new CustomEvent("aura:toast", {
            detail: { message: "Copied — paste it wherever 💜" },
          }));
        } catch (_) { /* ignore */ }
      }
    } catch (e) {
      // User cancelled — not an error
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      className="share-nudge-pill"
      data-testid="share-nudge-pill"
      onClick={handleOpen}
      disabled={opening}
    >
      <span className="share-nudge-icon" aria-hidden>{icon}</span>
      <span className="share-nudge-blurb">{blurb}</span>
      <span className="share-nudge-cta">📤</span>
      <span
        className="nudge-dismiss"
        data-testid="share-nudge-dismiss"
        onClick={handleDismiss}
        role="button"
        aria-label="Dismiss share suggestion"
      >×</span>
    </button>
  );
}
